import { useState, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { uomsApi, type UomDto } from "@/api/endpoints";

interface UomPickerProps {
  value: UomDto | null;
  onChange: (v: UomDto | null) => void;
  /** When parent has only an ID (e.g. editing existing item), the picker resolves it to a UomDto. */
  initialId?: string | null;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 200) {
  const [d, setD] = useState(value);
  useMemo(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);
  return d;
}

export function UomPicker({
  value, onChange, initialId, label = "Base UoM",
  required, error, helperText, disabled,
}: UomPickerProps) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);

  // Load all UoMs (small set, pageSize=100 is fine for the master)
  const q = useQuery({
    queryKey: ["uoms-picker", debounced],
    queryFn: () => uomsApi.list({ search: debounced || undefined, page: 1, pageSize: 100 }),
    staleTime: 5 * 60_000,
  });

  // Resolve initialId once the list loads, if parent didn't pass a value
  const resolved = !value && initialId && q.data
    ? q.data.items.find(u => u.id === initialId) ?? null
    : value;

  return (
    <Autocomplete<UomDto>
      value={resolved}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
